import { useEffect, useState } from 'react';
import { menusApi } from '../api/resources';

/**
 * Fetch a CMS-managed menu for a given location (e.g. "header", "footer").
 * The API returns a list of menus; we pick the first matching the location.
 *
 * Returns { items, loaded }:
 *   - items: normalized menu items (label, url, target, children) or [] if none
 *   - loaded: true once the request settles (success or failure)
 *
 * Consumers should fall back to their static nav when `loaded && items.length === 0`.
 */
export function useMenu(location) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    menusApi
      .list(location)
      .then((menus) => {
        if (!active) return;
        const menu = Array.isArray(menus)
          ? menus.find((m) => m.location === location) || menus[0]
          : null;
        setItems(normalizeItems(menu?.items));
      })
      .catch(() => {
        if (active) setItems([]);
      })
      .finally(() => {
        if (active) setLoaded(true);
      });
    return () => {
      active = false;
    };
  }, [location]);

  return { items, loaded };
}

function normalizeItems(rawItems) {
  if (!Array.isArray(rawItems)) return [];
  return rawItems
    .filter((it) => it && it.is_active !== false)
    .map((it) => ({
      id: it.id,
      label: it.label,
      url: it.resolved_url || it.url || '#',
      target: it.target || '_self',
      children: normalizeItems(it.children),
    }));
}

/** True for absolute/external URLs that must use a plain <a> rather than router Link. */
export function isExternalUrl(url) {
  return /^(?:[a-z]+:)?\/\//i.test(url) || /^(?:mailto|tel):/i.test(url);
}
